/**
 * Artikelstamm der Kasse in IndexedDB (Express Kunde 1: rund 20.000 Artikel). Der bisherige
 * Zwischenspeicher im localStorage fasst nur wenige MB und würde bei großen Stämmen mit
 * „QuotaExceeded“ scheitern; IndexedDB hat diese Grenze nicht. Eigene Datenbank, damit der
 * Offline-Belegpuffer (offline.ts, Datenbank „bilanx-kassa“) unberührt bleibt.
 *
 * Gespeichert wird der Rohwert (unknown); die Prüfung macht ladeStammCache in schnellwahl.ts.
 */
const DB_NAME = 'bilanx-kassa-stamm';
const STORE = 'stamm';

export interface StammAblage {
  lies(kassenId: string): Promise<unknown>;
  schreibe(kassenId: string, wert: unknown): Promise<void>;
}

function anfrage<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((loese, lehneAb) => {
    req.onsuccess = () => {
      loese(req.result);
    };
    req.onerror = () => {
      lehneAb(req.error ?? new Error('IndexedDB-Fehler'));
    };
  });
}

export class IndexedDbStammAblage implements StammAblage {
  private db: Promise<IDBDatabase> | null = null;

  constructor(private readonly indexedDb: IDBFactory) {}

  private oeffne(): Promise<IDBDatabase> {
    this.db ??= new Promise((loese, lehneAb) => {
      const req = this.indexedDb.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(STORE);
      };
      req.onsuccess = () => {
        loese(req.result);
      };
      req.onerror = () => {
        lehneAb(req.error ?? new Error('IndexedDB nicht verfügbar'));
      };
    });
    return this.db;
  }

  async lies(kassenId: string): Promise<unknown> {
    const db = await this.oeffne();
    return anfrage(db.transaction(STORE, 'readonly').objectStore(STORE).get(kassenId));
  }

  async schreibe(kassenId: string, wert: unknown): Promise<void> {
    const db = await this.oeffne();
    const tx = db.transaction(STORE, 'readwrite');
    const fertig = new Promise<void>((loese, lehneAb) => {
      tx.oncomplete = () => {
        loese();
      };
      tx.onerror = () => {
        lehneAb(tx.error ?? new Error('IndexedDB-Transaktion fehlgeschlagen'));
      };
      tx.onabort = () => {
        lehneAb(tx.error ?? new Error('IndexedDB-Transaktion abgebrochen'));
      };
    });
    tx.objectStore(STORE).put(wert, kassenId);
    await fertig;
  }
}

/** Für Tests und Umgebungen ohne IndexedDB. */
export class SpeicherStammAblage implements StammAblage {
  readonly werte = new Map<string, unknown>();

  lies(kassenId: string): Promise<unknown> {
    return Promise.resolve(this.werte.get(kassenId));
  }

  schreibe(kassenId: string, wert: unknown): Promise<void> {
    this.werte.set(kassenId, wert);
    return Promise.resolve();
  }
}

let standard: StammAblage | null = null;

/** Ablage des Browsers (IndexedDB), sonst null. */
export function stammAblage(): StammAblage | null {
  if (standard === null && typeof indexedDB !== 'undefined') {
    standard = new IndexedDbStammAblage(indexedDB);
  }
  return standard;
}
